import { Compass, ShieldCheck, Microscope, Quote } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/Primitives";
import { CalendlyButton } from "@/components/Calendly";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_restore-sleep/artifacts/6wvtdxe6_justin%202.webp";

const About = () => {
  return (
    <main data-testid="about-page" className="bg-white text-ink-900">
      {/* Hero */}
      <section className="relative pt-36 pb-16 md:pb-24 hero-halo">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-12 items-center">
            <Reveal className="col-span-12 lg:col-span-7">
              <SectionEyebrow testId="about-page-eyebrow">About</SectionEyebrow>
              <h1 className="mt-6 font-display font-light text-4xl sm:text-5xl md:text-6xl tracking-[-0.02em] leading-[1.05] text-ink-900 break-words">
                Built on
                <br />
                <span className="text-brand-600">his own body.</span>
              </h1>
              <p className="mt-7 max-w-xl text-ink-600 text-lg leading-relaxed">
                In June 2022, Justin was diagnosed with sleep apnea. What
                followed wasn&apos;t a quick fix — it was years of testing,
                iteration, and refinement. The system you see today was earned.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="col-span-12 lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-ink-200 shadow-[0_8px_30px_rgba(15,23,42,0.06)] bg-ink-50">
                <img
                  src={PORTRAIT}
                  alt="Justin Chen, founder of Restore.JC"
                  data-testid="about-portrait"
                  className="w-full h-auto block"
                />
              </div>
              <div className="mt-4 flex items-baseline gap-3">
                <span className="text-[10px] uppercase tracking-[0.25em] text-ink-500 font-display">
                  Founder · Coach
                </span>
                <span className="text-ink-300">/</span>
                <span className="font-display text-ink-900">Justin Chen</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Story column */}
      <section className="relative py-24 md:py-32 bg-ink-50 border-y border-ink-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 lg:col-span-3">
                <span className="font-display text-[10px] uppercase tracking-[0.25em] text-ink-500">
                  Origin
                </span>
                <div className="mt-3 font-display text-3xl font-light text-ink-900">
                  2022 →
                </div>
              </div>
              <div className="col-span-12 lg:col-span-9 space-y-6">
                <p className="font-display font-light text-2xl md:text-3xl tracking-tight leading-[1.25] text-ink-900">
                  Justin tested everything — meditation, lifestyle audits,
                  routines, supplements, devices, protocols.
                </p>
                <p className="text-ink-600 text-lg leading-relaxed">
                  Some moved the needle. Most didn&apos;t. The work was
                  separating signal from noise — and refining the few
                  interventions that consistently produced deep, restorative
                  sleep.
                </p>
                <p className="text-ink-900 text-lg leading-relaxed">
                  Today, he sleeps deeply, performs at his peak, and brings the
                  same precision to clients in tech, finance, and high-pressure
                  industries.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Principles */}
      <section
        data-testid="principles-section"
        className="relative py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionEyebrow testId="principles-eyebrow">Operating Principles</SectionEyebrow>
            <h2 className="mt-6 font-display font-light text-3xl sm:text-4xl md:text-5xl tracking-tight text-ink-900 leading-[1.1] max-w-3xl">
              How I work with clients.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Microscope,
                title: "Evidence over opinion",
                desc: "Every recommendation is rooted in published research, clinical protocols, and validated client outcomes — not trends.",
              },
              {
                icon: Compass,
                title: "Personalized, not protocoled",
                desc: "No copy-paste plans. Your sleep, biology, schedule, and goals shape the program from session one.",
              },
              {
                icon: ShieldCheck,
                title: "Results before retention",
                desc: "I'd rather you graduate than stay forever. The goal is autonomy — a system you own for life.",
              },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div
                  data-testid={`principle-card-${i}`}
                  className="surface-soft p-7 h-full"
                >
                  <div className="h-11 w-11 rounded-xl bg-brand-50 flex items-center justify-center">
                    <p.icon size={20} className="text-brand-600" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-medium text-ink-900">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-ink-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="relative py-24 md:py-32 bg-ink-50 border-y border-ink-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <Quote className="text-brand-600/60" size={36} strokeWidth={1.4} />
            <blockquote className="mt-6 font-display font-light text-2xl sm:text-3xl md:text-4xl tracking-tight leading-[1.2] text-ink-900 break-words">
              &ldquo;Sleep isn&apos;t a luxury — it&apos;s the foundation.
              Fix it, and the rest of your performance compounds.&rdquo;
            </blockquote>
            <div className="mt-8 text-xs text-ink-500 uppercase tracking-[0.25em] font-display">
              Justin Chen — Founder, Restore.JC
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display font-light text-3xl sm:text-4xl md:text-5xl tracking-tight text-ink-900 leading-[1.1]">
              Ready to work together?
            </h2>
            <p className="mt-5 text-ink-600 max-w-xl mx-auto leading-relaxed">
              Book a free assessment and we&apos;ll see if we&apos;re the right fit.
            </p>
            <div className="mt-9 flex justify-center">
              <CalendlyButton testId="about-bottom-cta">Book a Call</CalendlyButton>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default About;
